import produce from "immer";
import i18n from "../../i18n";
import {
  SCOPE_CSV_IMPORT_STARTED,
  SCOPE_CSV_IMPORT_QUEUED,
  SCOPE_CSV_IMPORT_PROGRESS,
  SCOPE_CSV_IMPORT_FINALIZING,
  SCOPE_CSV_IMPORT_ALL_COMPLETED,
  SCOPE_CSV_IMPORT_ALL_FAILED,
  SCOPE_CSV_IMPORT_JOB_DISMISS,
  SCOPE_CSV_IMPORT_REPORT,
} from "../types";
import { isImportReport } from "../../components/ImportReport/importReport";

/**
 * Scope-library CSV import progress slice.
 * Shape: { jobs: { [jobId]: JobState } } — the multi-job pattern the
 * ProgressNotificationStack selector consumes. Progress is count-based
 * (scopes persisted / total), not per-item: the backend streams batch
 * frames with a running `current`.
 */
const initialJobState = {
  active: false,
  startedAt: null,
  terminatedAt: null,
  totalProjects: 0,
  completedCount: 0,
  failedCount: 0,
  etaSeconds: null,
  avgSecondsPerWeight: null,
  error: null,
  fileName: null,
  finalizing: false,
  finalizingScopes: null,
  // Final CsvValidationReportDTO (counters + warnings, or the row-level
  // errors of a rejected file) once known — the card's detail view reads it.
  report: null,
};

/**
 * A validation failure stores its full row-level report as JSON in the job's
 * registry `error` column (the terminal WS frame stays small). The generic
 * rehydration/polling paths surface that column verbatim, so turn it back
 * into one readable sentence here — the row list is fetched separately and
 * rendered in the import modal.
 */
function readableJobError(error) {
  if (typeof error !== "string" || !error.startsWith("{")) return error;
  try {
    const parsed = JSON.parse(error);
    if (!Array.isArray(parsed?.errors)) return error;
    const count = parsed.errorCount ?? parsed.errors.length;
    return i18n.t("csvImportModal.validationFailedSummary", {
      count,
      defaultValue: `${count} validation error(s) — nothing was imported.`,
    });
  } catch {
    return error;
  }
}

export const initialState = { jobs: {} };

export default function structureCsvImportReducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case SCOPE_CSV_IMPORT_STARTED:
      return produce(state, (draft) => {
        // Merge onto an existing job so a rehydration STARTED updates the
        // provided fields without zeroing live progress.
        const prev = draft.jobs[payload.jobId];
        draft.jobs[payload.jobId] = {
          ...initialJobState,
          ...(prev ?? {}),
          active: true,
          jobId: payload.jobId,
          startedAt: prev?.startedAt ?? payload.startedAt ?? 0,
          fileName: payload.fileName ?? prev?.fileName ?? null,
          totalProjects: payload.totalProjects ?? prev?.totalProjects ?? 0,
          // Monotonic: a merge from the job registry (rehydration poll) can
          // carry a count older than the live WS one — the card must never
          // count backwards.
          completedCount: Math.max(
            payload.completedCount ?? 0,
            prev?.completedCount ?? 0
          ),
          etaSeconds: payload.etaSeconds ?? prev?.etaSeconds ?? null,
        };
      });

    case SCOPE_CSV_IMPORT_QUEUED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        job.totalProjects = payload.total ?? 0;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
        if (payload.avgSecondsPerWeight != null) job.avgSecondsPerWeight = payload.avgSecondsPerWeight;
      });

    case SCOPE_CSV_IMPORT_PROGRESS:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        // Frames can race; counts only move forward.
        if (payload.completedCount != null && payload.completedCount > job.completedCount) {
          job.completedCount = payload.completedCount;
        }
        if (payload.total != null && payload.total > 0) job.totalProjects = payload.total;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case SCOPE_CSV_IMPORT_FINALIZING:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        job.finalizing = true;
        if (payload.total != null && payload.total > 0) job.finalizingScopes = payload.total;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case SCOPE_CSV_IMPORT_ALL_COMPLETED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload?.jobId];
        if (!job) return;
        job.active = false;
        job.finalizing = false;
        job.finalizingScopes = null;
        if (payload?.completedCount != null) job.completedCount = payload.completedCount;
        if (isImportReport(payload?.report)) job.report = payload.report;
        job.etaSeconds = 0;
        job.terminatedAt = payload?.terminatedAt ?? Date.now();
      });

    case SCOPE_CSV_IMPORT_ALL_FAILED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload?.jobId];
        if (!job) return;
        job.active = false;
        job.finalizing = false;
        job.finalizingScopes = null;
        job.error = readableJobError(payload?.error) || "Import failed";
        if (isImportReport(payload?.report)) job.report = payload.report;
        job.terminatedAt = payload?.terminatedAt ?? Date.now();
      });

    case SCOPE_CSV_IMPORT_REPORT:
      return produce(state, (draft) => {
        const job = draft.jobs[payload?.jobId];
        if (!job || !isImportReport(payload?.report)) return;
        job.report = payload.report;
      });

    case SCOPE_CSV_IMPORT_JOB_DISMISS:
      return produce(state, (draft) => {
        if (payload?.jobId === "all") {
          Object.keys(draft.jobs).forEach((id) => {
            if (!draft.jobs[id].active) delete draft.jobs[id];
          });
        } else if (payload?.jobId) {
          delete draft.jobs[payload.jobId];
        }
      });

    default:
      return state;
  }
}
